import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { NotificationSettingsEditor } from '../../../components/notification-settings';
import {
  useAccountNotificationSettings,
  useResetAccountNotificationSettings,
  useSaveAccountNotificationSettings,
} from '../../../queries/use-notification-settings';
import { JsonSection } from './json-section';

interface NotificationSettingsTabProps {
  accountId: string;
}

export function NotificationSettingsTab({ accountId }: NotificationSettingsTabProps) {
  const { data, isLoading, isError, error } = useAccountNotificationSettings(accountId);
  const saveForAccount = useSaveAccountNotificationSettings(accountId);
  const resetAccount = useResetAccountNotificationSettings(accountId);

  const confirmReset = (): void =>
    openConfirmModal({
      title: 'Reset to global?',
      children: (
        <Text size="sm">
          Removes every switch and template this account sets, so it follows the global settings.
        </Text>
      ),
      labels: { confirm: 'Reset', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        resetAccount.mutate(undefined, {
          onError: (resetError) =>
            showNotification({ title: 'Reset failed', message: resetError.message, color: 'red' }),
        }),
    });

  return data ? (
    <Stack spacing="md">
      <NotificationSettingsEditor
        key={accountId}
        response={data}
        scope="account"
        saving={saveForAccount.isPending}
        onSave={(changes) => saveForAccount.mutateAsync(changes)}
        actions={
          <Button
            variant="subtle"
            color="red"
            size="xs"
            disabled={!data.stored}
            loading={resetAccount.isPending}
            onClick={confirmReset}
          >
            Reset to global
          </Button>
        }
      />
      <JsonSection title="Stored account document" data={data.stored ?? undefined} />
    </Stack>
  ) : isLoading ? (
    <Center mih={200}>
      <Loader />
    </Center>
  ) : isError ? (
    <Text color="red">
      Failed to load notification settings: {(error as Error)?.message ?? 'Unknown error'}
    </Text>
  ) : (
    <Text color="dimmed">No notification settings found.</Text>
  );
}
